import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ImageUrlPipeModule } from '@rd/core/services/url-utils/image-url.pipe';
import { ButtonModule } from '@rd/shared-legacy/components/button/button.module';
import { IconModule } from '@rd/shared-legacy/components/icon/icon.module';
import { HttpError } from '@rd/types';
import { Promocode, PromocodeUsage } from '@rd/types/promocode.type';
import { Subject } from 'rxjs';
import { delay } from 'rxjs/operators';
@UntilDestroy()
@Component({
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    IconModule,
    ImageUrlPipeModule,
  ],
  selector: 'rd-promocode-ui',
  templateUrl: './promocode-ui.component.html',
  styleUrls: ['./promocode-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromocodeUiComponent implements OnInit {
  promocodeForm = this.fb.group({
    promocode: [{ value: '', disabled: false }],
  });

  promocodeApplied = false;

  promocodeError: string | null = null;

  @Input() promocodeLoading = false;

  @Input() set promocode(promocode: Promocode | null) {
    this.promocodeForm.patchValue(
      {
        promocode: promocode?.code ?? '',
      },
      { emitEvent: false },
    );
  }

  private _promocodeUsage: PromocodeUsage | null = null;
  @Input() set promocodeUsage(promocodeUsage: PromocodeUsage | null) {
    this._promocodeUsage = promocodeUsage;
    if (this._promocodeUsage) {
      if (this.hasValidPromocode()) {
        this.promocodeForm.get('promocode')?.disable({ emitEvent: false });
        this.promocodeForm.get('promocode')?.setErrors(null);
        this.promocodeError = null;

        // Focus on alert message appeared on screen
        this.focusOnAlertMessage.next();
      }
      if (this.hasExpiredPromocode()) {
        // @ts-ignore
        this.promocodeError = this.transformPromocodeInvalidMessage(
          this._promocodeUsage.remark,
        );
        this.promocodeForm.get('promocode')?.enable({ emitEvent: false });
        this.promocodeExpired.emit();

        // Focus on alert message appeared on screen
        this.focusOnAlertMessage.next();
      }
    } else {
      this.promocodeForm.get('promocode')?.enable({ emitEvent: false });
      this.promocodeForm.get('promocode')?.setErrors(null);
      this.promocodeError = null;
    }
  }

  @Input() set promocodeHttpError(error: HttpError | null) {
    const promocodeErrorMessage = this.getPromocodeInvalidMessage(error);
    if (promocodeErrorMessage) {
      this.promocodeError = promocodeErrorMessage;
      this.promocodeForm.get('promocode')?.markAsTouched();
      this.promocodeForm.get('promocode')?.setErrors({ invalid: true });

      // Focus on alert message appeared on screen
      this.focusOnAlertMessage.next();
    }
  }

  @Output() applyPromocode = new EventEmitter<string>();

  @Output() promocodeExpired = new EventEmitter();

  @ViewChild('alertMessage') alertMessage?: ElementRef<HTMLSpanElement>;

  private focusOnAlertMessage = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.promocodeForm
      .get('promocode')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((promocode) => {
        this.promocodeForm.patchValue(
          {
            promocode: (promocode ?? '').toUpperCase().replace(/\s/g, ''),
          },
          { emitEvent: false },
        );

        if (this.promocodeError) {
          this.promocodeError = null;
        }
      });

    this.focusOnAlertMessage
      .pipe(
        // We use delay(0) to wait for the Change Detection to finish running
        // and the alert message is showed on the screen so we can focus on it
        delay(0),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.alertMessage?.nativeElement.focus();
      });
  }

  submit() {
    if (this.hasValidPromocode()) {
      this.applyPromocode.emit();
    } else {
      const promocodeValue = this.promocodeForm.get('promocode')?.value;
      if (promocodeValue) {
        this.promocodeApplied = true;
        this.applyPromocode.emit(promocodeValue);
      }
    }
  }

  get hasValue(): boolean {
    return this.promocodeForm.get('promocode')?.value !== '';
  }

  getButtonName(): string {
    return this.hasValidPromocode()
      ? 'promocode.button.remove'
      : 'promocode.button.apply';
  }

  getButtonAriaLabel(): string {
    return this.hasValidPromocode()
      ? 'promocode.button.remove.aria_label'
      : 'promocode.button.apply.aria_label';
  }

  hasDiscount(): boolean {
    return (
      !!this._promocodeUsage &&
      (this._promocodeUsage.pointsDiscount > 0 ||
        this._promocodeUsage.cashDiscount > 0)
    );
  }

  canShowPromocodeSuccessMessage(): boolean {
    return (
      this.promocodeApplied &&
      this._promocodeUsage?.status === 'applied' &&
      !this.promocodeError
    );
  }

  private getPromocodeInvalidMessage(
    promocodeError: HttpError | null,
  ): string | null {
    // @ts-ignore
    if (promocodeError) {
      if (promocodeError?.errors) {
        return this.transformPromocodeInvalidMessage(
          promocodeError?.errors[0].metadata?.remark,
        );
      } else {
        return this.transformPromocodeInvalidMessage(
          'promocode_unexpected_error',
        );
      }
    } else {
      return null;
    }
  }

  private transformPromocodeInvalidMessage(key: string | null): string {
    return this.translateService.instant(
      `promocode.error.${key ? key : 'promocode_unexpected_error'}`,
    );
  }

  private hasValidPromocode(): boolean {
    const codeStatus = this._promocodeUsage?.status;
    return codeStatus === 'applied' || codeStatus === 'attached';
  }

  private hasExpiredPromocode(): boolean {
    const codeStatus = this._promocodeUsage?.status;
    return codeStatus === 'voided';
  }
}
